import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useVoiceRecorder } from '../hooks/useVoiceRecorder';
import { transcribeAndExtract, createMeeting } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { 
  ArrowLeft, 
  Microphone, 
  Stop, 
  Pause, 
  Play,
  SpinnerGap,
  Sparkle,
  CalendarBlank,
  Waveform,
  CheckCircle,
  ArrowRight
} from '@phosphor-icons/react';
import { format } from 'date-fns';

export default function VoiceMeetingPage() {
  const navigate = useNavigate();
  const {
    isRecording,
    isPaused,
    recordingTime,
    audioBlob,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    resetRecording,
    formatTime
  } = useVoiceRecorder();

  const [step, setStep] = useState('record'); // record, processing, review, saving
  const [transcription, setTranscription] = useState('');
  const [error, setError] = useState('');
  const [date, setDate] = useState(new Date());
  
  const [formData, setFormData] = useState({
    client_name: '',
    client_email: '',
    client_company: '',
    meeting_type: 'call',
    key_points: '',
    next_steps: '',
    deal_value: '',
    deal_stage: 'prospecting',
    notes: ''
  });

  const handleStartRecording = async () => {
    try {
      setError('');
      await startRecording();
    } catch (err) {
      setError('Failed to access microphone. Please allow microphone access.');
    }
  };

  const handleStopAndProcess = async () => {
    setStep('processing');
    setError('');
    
    try {
      // stopRecording now returns a Promise with the blob
      const blob = await stopRecording();
      
      if (!blob || blob.size === 0) {
        setError('No recording found. Please try again.');
        setStep('record');
        return;
      }

      const response = await transcribeAndExtract(blob);
      const { transcription: text, extracted_data } = response.data;
      
      setTranscription(text);
      
      // Fill form with extracted data
      if (extracted_data) {
        setFormData(prev => ({
          ...prev,
          client_name: extracted_data.client_name || prev.client_name,
          client_email: extracted_data.client_email || prev.client_email,
          client_company: extracted_data.client_company || prev.client_company,
          meeting_type: extracted_data.meeting_type || prev.meeting_type,
          key_points: extracted_data.key_points || prev.key_points,
          next_steps: extracted_data.next_steps || prev.next_steps,
          deal_value: extracted_data.deal_value ? String(extracted_data.deal_value) : prev.deal_value,
          deal_stage: extracted_data.deal_stage || prev.deal_stage,
          notes: extracted_data.notes || prev.notes
        }));
      }
      
      setStep('review');
    } catch (err) {
      console.error('Processing error:', err);
      const detail = err.response?.data?.detail || '';
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        setError('Recording processing timed out. Try a shorter recording or check your connection.');
      } else if (detail.includes('key') || detail.includes('configured')) {
        setError('AI service is temporarily unavailable. Please try again later.');
      } else {
        setError(detail || 'Failed to process recording. Please try again.');
      }
      setStep('record');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveMeeting = async () => {
    if (!formData.client_name || !formData.key_points) {
      setError('Client name and key points are required');
      return;
    }

    setStep('saving');
    setError('');

    try {
      const meetingData = {
        ...formData,
        meeting_date: date.toISOString(),
        deal_value: formData.deal_value ? parseFloat(formData.deal_value) : null
      };
      
      await createMeeting(meetingData);
      navigate('/meetings');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save meeting');
      setStep('review');
    }
  };

  const handleRetry = () => {
    resetRecording();
    setTranscription('');
    setFormData({
      client_name: '',
      client_email: '',
      client_company: '',
      meeting_type: 'call',
      key_points: '',
      next_steps: '',
      deal_value: '',
      deal_stage: 'prospecting',
      notes: ''
    });
    setStep('record');
  };

  return (
    <div className="max-w-3xl mx-auto animate-entrance" data-testid="voice-meeting-page">
      {/* Header */}
      <div className="mb-8">
        <button 
          onClick={() => navigate(-1)} 
          className="flex items-center gap-2 text-[#86868B] hover:text-[#1D1D1F] transition-colors mb-4"
        >
          <ArrowLeft size={20} />
          <span>Back</span>
        </button>
        <h1 className="text-3xl font-bold text-[#1D1D1F]">
          Voice Meeting
        </h1>
        <p className="text-[#86868B] mt-2">
          Record your meeting summary and AI will extract all the details automatically.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm mb-6" data-testid="voice-error">
          {error}
        </div>
      )}

      {/* Step 1: Recording */}
      {step === 'record' && (
        <div className="card-base p-8 text-center">
          <div className="mb-8">
            <div className={`w-32 h-32 mx-auto rounded-full flex items-center justify-center transition-all ${
              isRecording 
                ? 'bg-[#FF3B30] animate-pulse' 
                : 'bg-[#007AFF]'
            }`}>
              {isRecording ? (
                <Waveform size={56} className="text-white" weight="bold" />
              ) : (
                <Microphone size={56} className="text-white" weight="bold" />
              )}
            </div>
          </div>

          {isRecording && (
            <div className="mb-6">
              <p className="text-4xl font-mono font-bold text-[#1D1D1F]">
                {formatTime(recordingTime)}
              </p>
              <p className="text-sm text-[#86868B] mt-2">
                {isPaused ? 'Paused' : 'Recording...'}
              </p>
            </div>
          )}

          <div className="flex items-center justify-center gap-4">
            {!isRecording ? (
              <Button 
                onClick={handleStartRecording}
                className="btn-primary h-12 px-8 rounded-xl text-lg"
                data-testid="start-recording-btn"
              >
                <Microphone size={24} className="mr-2" />
                Start Recording
              </Button>
            ) : (
              <>
                <Button 
                  onClick={isPaused ? resumeRecording : pauseRecording}
                  variant="outline"
                  className="btn-secondary h-12 w-12 p-0 rounded-xl"
                  data-testid="pause-recording-btn"
                >
                  {isPaused ? <Play size={24} /> : <Pause size={24} />}
                </Button>
                <Button 
                  onClick={handleStopAndProcess}
                  className="btn-accent h-12 px-8 rounded-xl text-lg"
                  data-testid="stop-recording-btn"
                >
                  <Stop size={24} className="mr-2" />
                  Stop & Process
                </Button>
              </>
            )}
          </div>

          <p className="text-sm text-[#86868B] mt-8">
            Describe your meeting: client name, company, what you discussed, next steps, and deal details.
          </p>
        </div>
      )}

      {/* Step 2: Processing */}
      {step === 'processing' && (
        <div className="card-base p-12 text-center">
          <div className="w-20 h-20 mx-auto mb-6 bg-[#007AFF]/10 rounded-full flex items-center justify-center">
            <SpinnerGap size={40} className="text-[#007AFF] animate-spin" />
          </div>
          <h2 className="text-xl font-semibold text-[#1D1D1F] mb-2">
            Processing your recording...
          </h2>
          <p className="text-[#86868B]">
            Transcribing audio and extracting meeting details with AI
          </p>
        </div>
      )}

      {/* Step 3: Review & Edit */}
      {(step === 'review' || step === 'saving') && (
        <div className="space-y-6">
          {/* Transcription */}
          <div className="card-base p-6">
            <div className="flex items-center gap-2 mb-4">
              <CheckCircle size={20} className="text-[#34C759]" />
              <h2 className="text-lg font-semibold text-[#1D1D1F]">
                Transcription
              </h2>
            </div>
            <div className="ai-preview max-h-32 overflow-y-auto">
              {transcription}
            </div>
          </div>

          {/* Extracted Data Form */}
          <div className="card-base p-6 space-y-6">
            <div className="flex items-center gap-2 mb-2">
              <Sparkle size={20} className="text-[#007AFF]" />
              <h2 className="text-lg font-semibold text-[#1D1D1F]">
                Extracted Details
              </h2>
            </div>
            <p className="text-sm text-[#86868B] -mt-4 mb-4">
              Review and edit the AI-extracted information before saving.
            </p>
            
            <div className="grid sm:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="client_name" className="text-[#1D1D1F] font-medium">
                  Client Name <span className="text-[#FF3B30]">*</span>
                </Label>
                <Input
                  id="client_name"
                  name="client_name"
                  value={formData.client_name}
                  onChange={handleChange}
                  placeholder="John Smith"
                  required
                  className="input-base h-12 rounded-xl"
                  data-testid="voice-client-name"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="client_email" className="text-[#1D1D1F] font-medium">
                  Client Email
                </Label>
                <Input
                  id="client_email"
                  name="client_email"
                  type="email"
                  value={formData.client_email}
                  onChange={handleChange}
                  placeholder="john@company.com"
                  autoComplete="email"
                  inputMode="email"
                  className="input-base h-12 rounded-xl text-base"
                  data-testid="voice-client-email"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="client_company" className="text-[#1D1D1F] font-medium">
                  Company
                </Label>
                <Input
                  id="client_company"
                  name="client_company"
                  value={formData.client_company}
                  onChange={handleChange}
                  placeholder="Acme Corporation"
                  className="input-base h-12 rounded-xl"
                  data-testid="voice-client-company"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-[#1D1D1F] font-medium">Meeting Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button 
                      variant="outline" 
                      className="w-full justify-start text-left font-normal h-12 rounded-xl border-gray-200 hover:bg-gray-50"
                    >
                      <CalendarBlank size={18} className="mr-2 text-gray-400" />
                      {format(date, 'PPP')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={(d) => d && setDate(d)}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <Label className="text-[#1D1D1F] font-medium">Meeting Type</Label>
                <Select value={formData.meeting_type} onValueChange={(v) => handleSelectChange('meeting_type', v)}>
                  <SelectTrigger className="h-12 rounded-xl border-gray-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="call">Phone Call</SelectItem>
                    <SelectItem value="video">Video Call</SelectItem>
                    <SelectItem value="in-person">In-Person</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-[#1D1D1F] font-medium">Deal Stage</Label>
                <Select value={formData.deal_stage} onValueChange={(v) => handleSelectChange('deal_stage', v)}>
                  <SelectTrigger className="h-12 rounded-xl border-gray-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="prospecting">Prospecting</SelectItem>
                    <SelectItem value="qualification">Qualification</SelectItem>
                    <SelectItem value="proposal">Proposal</SelectItem>
                    <SelectItem value="negotiation">Negotiation</SelectItem>
                    <SelectItem value="closed">Closed Won</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="deal_value" className="text-[#1D1D1F] font-medium">
                  Deal Value ($)
                </Label>
                <Input
                  id="deal_value"
                  name="deal_value"
                  type="number"
                  value={formData.deal_value}
                  onChange={handleChange}
                  placeholder="10000"
                  className="input-base h-12 rounded-xl"
                  data-testid="voice-deal-value"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="key_points" className="text-[#1D1D1F] font-medium">
                Key Points <span className="text-[#FF3B30]">*</span>
              </Label>
              <Textarea
                id="key_points"
                name="key_points"
                value={formData.key_points}
                onChange={handleChange}
                placeholder="Main discussion points..."
                required
                rows={4}
                className="input-base rounded-xl resize-none"
                data-testid="voice-key-points"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="next_steps" className="text-[#1D1D1F] font-medium">
                Next Steps
              </Label>
              <Textarea
                id="next_steps"
                name="next_steps"
                value={formData.next_steps}
                onChange={handleChange}
                placeholder="Action items and follow-ups..."
                rows={3}
                className="input-base rounded-xl resize-none"
                data-testid="voice-next-steps"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes" className="text-[#1D1D1F] font-medium">
                Additional Notes
              </Label>
              <Textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                placeholder="Any other relevant information..."
                rows={2}
                className="input-base rounded-xl resize-none"
                data-testid="voice-notes"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between pt-4">
            <Button 
              type="button" 
              variant="outline" 
              onClick={handleRetry}
              disabled={step === 'saving'}
              className="btn-secondary h-12 px-6 rounded-xl"
            >
              <Microphone size={18} className="mr-2" />
              Record Again
            </Button>
            <Button 
              onClick={handleSaveMeeting}
              disabled={step === 'saving'}
              className="btn-primary h-12 px-8 rounded-xl"
              data-testid="save-voice-meeting-btn"
            >
              {step === 'saving' ? (
                <>
                  <SpinnerGap size={20} className="mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  Save Meeting
                  <ArrowRight size={20} className="ml-2" />
                </>
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
